<template>
  <div class="wg-content-only-detail">
    <router-link
      :to="{ name: 'AllContentWhiteGlove', params: { siteId: String(siteId) } }"
      class="wg-content-only-detail__back"
    >
      <i class="material-icons wg-content-only-detail__back-icon">arrow_back</i>
      <span>All content</span>
    </router-link>

    <template v-if="silo">
      <div class="wg-content-only-detail__head">
        <div class="wg-content-only-detail__title-row">
          <h1 class="wg-content-only-detail__title">{{ silo.name }}</h1>
          <span class="wg-content-only-detail__badge wg-content-only-detail__badge--quick">Content only</span>
          <b-dropdown
            aria-role="list"
            class="wg-content-only-detail__page-type-dropdown"
            @click.native.stop
            :disabled="!whiteGloveMissionControlAdminUi"
          >
            <template #trigger>
              <button
                v-if="silo.pageType === 'new' || silo.pageType === 'existing'"
                type="button"
                class="wg-content-only-detail__badge wg-content-only-detail__badge--trigger"
              >
                <span>{{ pageTypeLabel }}</span>
                <i v-if="whiteGloveMissionControlAdminUi" class="material-icons-outlined wg-content-only-detail__badge-caret">expand_more</i>
              </button>
              <b-tooltip
                v-else-if="whiteGloveMissionControlAdminUi"
                label="Assign page type"
                type="is-dark"
              >
                <button
                  type="button"
                  class="wg-content-only-detail__badge wg-content-only-detail__badge--trigger wg-content-only-detail__badge--assign"
                >
                  <i class="material-icons-outlined wg-content-only-detail__badge-assign-icon">description</i>
                  <i class="material-icons-outlined wg-content-only-detail__badge-caret">expand_more</i>
                </button>
              </b-tooltip>
            </template>
            <b-dropdown-item
              aria-role="listitem"
              @click.native.stop="updateSilo({ pageType: 'new' }, true)"
            >
              New page
            </b-dropdown-item>
            <b-dropdown-item
              aria-role="listitem"
              @click.native.stop="updateSilo({ pageType: 'existing' }, true)"
            >
              Existing page
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <p class="wg-content-only-detail__path">{{ mainPagePathDisplay }}</p>
      </div>

      <div class="wg-content-only-detail__metrics">
        <div class="wg-content-only-detail__metric">
          <div class="wg-content-only-detail__metric-value">{{ keywordCount }} Keywords</div>
          <div class="wg-content-only-detail__metric-label">
            <span
              v-if="keywordMetricLabel"
              class="wg-content-only-detail__metric-label-breakdown"
            >{{ keywordMetricLabel }}</span>
          </div>
        </div>
        <div class="wg-content-only-detail__metric">
          <div class="wg-content-only-detail__metric-value">{{ estTrafficFormatted }}</div>
          <v-tooltip bottom color="transparent">
            <template v-slot:activator="{ on, attrs }">
              <div
                class="wg-content-only-detail__metric-label wg-content-only-detail__metric-label--tooltip"
                v-bind="attrs"
                v-on="on"
              >
                <span>Estimated visibility</span>
                <i class="material-icons wg-content-only-detail__metric-info-icon">info_outline</i>
              </div>
            </template>
            <div class="white-bg-tooltip small-tooltip wg-content-only-detail__emv-tooltip-bubble">
              <span class="wg-content-only-detail__emv-tooltip-text">Modeled estimate of how often this silo/content plan could appear in relevant Google searches based on search demand, topical coverage, and target rankings. This is not Search Console impression data.</span>
            </div>
          </v-tooltip>
        </div>
        <div class="wg-content-only-detail__metric" v-if="(this.keywordCount || 0) !== this.selectedOrderCount">
          <div class="wg-content-only-detail__metric-value" style="color: var(--v-profileAnchorColor-base);">{{ orderedSelectionLine }}</div>
          <div class="wg-content-only-detail__metric-label">{{ orderedSelectionMetricLabel }}</div>
        </div>
        <div
          v-if="showOrderMetricsCard"
          class="wg-content-only-detail__metric wg-content-only-detail__metric--wide wg-content-only-detail__order-card"
        >
          <h3 class="wg-content-only-detail__order-card-title">Your content reports order</h3>
          <!--
          <div
            v-if="whiteGloveMissionControlAdminUi && order.order_status === 'paid'"
            class="wg-content-only-detail__order-line wg-content-only-detail__order-line--admin"
          >
            <span class="wg-content-only-detail__order-k">Set content reports order status:</span>
            <div class="wg-content-only-detail__order-status-inline">
              <b-dropdown aria-role="list">
                <template #trigger>
                  <b-button
                    :label="getFulfillementStatusLabel(order.fulfillement_status)"
                    icon-right="angle-down"
                  />
                </template>
                <b-dropdown-item
                  v-if="order.fulfillement_status !== 'pending'"
                  aria-role="listitem"
                  @click="updateOrder({ set_pending: true })"
                >{{ getFulfillementStatusLabel('pending') }}</b-dropdown-item>
                <b-dropdown-item
                  v-if="order.fulfillement_status !== 'in_progress'"
                  aria-role="listitem"
                  @click="updateOrder({ set_in_progress: true })"
                >{{ getFulfillementStatusLabel('in_progress') }}</b-dropdown-item>
                <b-dropdown-item
                  v-if="order.fulfillement_status !== 'completed'"
                  aria-role="listitem"
                  @click="updateOrder({ set_completed: true })"
                >{{ getFulfillementStatusLabel('completed') }}</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
          <div v-else class="wg-content-only-detail__order-line">
            <span class="wg-content-only-detail__order-k">Order status:</span>
            <span class="wg-content-only-detail__order-v">{{ getFulfillementStatusLabel(order.fulfillement_status) }}</span>
            <template v-if="order.stripe_invoice_to_be_finalized_at">
              <div class="wg-content-only-detail__order-invoice-note wg-content-only-detail__order-line-break">
                (Not yet paid. Invoice is due: {{ new Date(order.stripe_invoice_to_be_finalized_at).toDateString() }})
              </div>
              <v-btn
                class="text-none wg-content-only-detail__cancel-order-btn wg-content-only-detail__order-line-break"
                small
                @click.stop="showCancelOrderModal = true"
              >
                Cancel this order
              </v-btn>
            </template>
          </div>
          -->
          <div
            v-if="order.stripe_invoice_to_be_finalized_at && !(whiteGloveMissionControlAdminUi && order.order_status === 'paid')"
            class="wg-content-only-detail__order-line"
          >
            <div class="wg-content-only-detail__order-invoice-note wg-content-only-detail__order-line-break">
              (Not yet paid. Invoice is due: {{ new Date(order.stripe_invoice_to_be_finalized_at).toDateString() }})
            </div>
            <v-btn
              class="text-none wg-content-only-detail__cancel-order-btn wg-content-only-detail__order-line-break"
              small
              @click.stop="showCancelOrderModal = true"
            >
              Cancel this order
            </v-btn>
          </div>
          <div v-if="order.delivery_period_starts_at" class="wg-content-only-detail__order-line">
            <span class="wg-content-only-detail__order-k">Delivery period start date:</span>
            <span class="wg-content-only-detail__order-v">{{ formatOrderDeliveryPeriodStart(order.delivery_period_starts_at) }}</span>
          </div>
          <div v-if="deliveryPeriodLabel" class="wg-content-only-detail__order-line">
            <span class="wg-content-only-detail__order-k">Delivery period:</span>
            <span class="wg-content-only-detail__order-v">{{ deliveryPeriodLabel }}</span>
          </div>
          <div v-if="order.due_date" class="wg-content-only-detail__order-line">
            <span class="wg-content-only-detail__order-k">Delivery due date:</span>
            <span class="wg-content-only-detail__order-v">{{ formatOrderDueAt(order.due_date) }}</span>
          </div>
        </div>
      </div>

      <div class="wg-content-only-detail__legend" aria-label="Keyword types legend">
        <span class="wg-content-only-detail__legend-title">Keyword types:</span>
        <span class="wg-content-only-detail__legend-item">
          <span class="wg-content-only-detail__tag wg-content-only-detail__tag--tl">TL</span> Top-level
        </span>
        <span class="wg-content-only-detail__legend-item">
          <span class="wg-content-only-detail__tag wg-content-only-detail__tag--stl">STL</span> Secondary top-level
        </span>
        <span class="wg-content-only-detail__legend-item">
          <span class="wg-content-only-detail__tag wg-content-only-detail__tag--s">S</span> Supporting
        </span>
        <span class="wg-content-only-detail__legend-hint">Click the type tag on any keyword to change it</span>
      </div>

      <div
        v-if="selectedSiloKeywords?.length"
        class="flex white-selected-box h78 justify-space-between mt-4"
      >
        <div class="box-primary-bg d-flex align-center">
          <div class="count-value px-5">
            {{ selectedSiloKeywords?.length || 0 }}
          </div>
        </div>
        <div class="px-3 d-flex align-center">
          <div class="selected-text">item{{ selectedSiloKeywords?.length === 1 ? '' : 's' }} selected</div>
        </div>
        <div class="d-flex flex-wrap align-center">
          <div class="flex gap15 align-center fill-height">
            <span
              v-if="isWhiteGloveUser && $refs?.['silo-result-table']?.isContentOrderEditable && $refs?.['silo-result-table']?.isValidKeywordSelection?.(selectedSiloKeywordsRows)"
              class="flex flex-column align-center cursor-pointer"
              style="width: 6rem;"
              @click="orderContentForSelectedKeywords()"
            >
              <i class="material-icons-outlined orange-color">shopping_basket</i>
              <div class="action-title">Order content</div>
            </span>
            <span
              v-if="!isWhiteGloveUser || (isWhiteGloveUser && isMissionControlSession)"
              class="flex flex-column align-center cursor-pointer w78"
              @click="bulkMovePopUp = true"
            >
              <i class="material-icons-outlined orange-color">swap_horiz</i>
              <div class="action-title">Move to another silo</div>
            </span>
            <span
              v-if="isWhiteGloveUser && isMissionControlSession"
              class="flex flex-column align-center cursor-pointer w78"
              @click="openBulkModal()"
            >
              <i class="material-icons-outlined orange-color">sync</i>
              <div class="action-title">Create POP run reports</div>
            </span>
            <!--<span
              v-if="isWhiteGloveUser && isMissionControlSession"
              class="flex flex-column align-center cursor-pointer w78"
            >
              <i class="material-icons-outlined flip-icon orange-color">list</i>
              <div class="action-title">Create AI Writer outlines</div>
            </span>-->
            <span
              v-if="!isWhiteGloveUser || (isWhiteGloveUser && isMissionControlSession)"
              class="flex flex-column align-center cursor-pointer w78"
              @click="bulkTrackKeyword()"
            >
              <i class="material-icons orange-color">add_circle_outline</i>
              <div class="action-title">Track</div>
            </span>
            <span
              v-if="!isWhiteGloveUser || (isWhiteGloveUser && isMissionControlSession)"
              class="flex flex-column align-center cursor-pointer w78"
              @click="bulkRemoveKeyword()"
            >
              <i class="material-icons-outlined orange-color">delete</i>
              <div class="action-title">Remove</div>
            </span>
            <span
              v-if="isWhiteGloveUser && isMissionControlSession"
              class="flex flex-column align-center cursor-pointer w78"
            >
              <img
                src="/static/star_group.svg"
                alt=""
                style="width: 24px; height: 24px"
              >
              <div class="action-title">Create AI Writer articles</div>
            </span>
            <div
              class="cross-container flex justify-center align-center fill-height px-5"
            >
              <i class="material-icons cursor-pointer" @click="resetSelectedSiloKeywords">close</i>
            </div>
          </div>
        </div>
      </div>

      <silo-result-table
        ref="silo-result-table"
        :isQuickContentOrderView="true"
        :siteId="siteId"
        :site="site"
        :silo="silo"
        :silo_keywords="activeKeywords"
        :deleted_silo_keywords="deletedKeywords"
        :refreshData="loadData"
        :allOrderedSiloKeywords="allOrderedSiloKeywords"
        :orderContentForSelectedKeywords="() => orderContentForSelectedKeywords()"
        :selectedSiloKeywordsRows="selectedSiloKeywordsRows"
        @onKeywordRemoveSuccess="onKeywordRemoveSuccess"
        @onKeywordRestoreSuccess="onKeywordRestoreSuccess"
      />

      <bulk-run-modal :bulk-pop-up="bulkPopUp" @submit="createBulkReport" @closeModal="closeBulkModal" />

      <bulk-move-to-silo-modal
        v-if="bulkMovePopUp"
        :silo="silo"
        :silo_keywords="silo_keywords"
        :selected-silo-keywords="selectedSiloKeywords"
        @close="bulkMovePopUp = false"
        @success="resetSelectedSiloKeywords(); loadData()"
      />

      <div v-if="showCancelOrderModal" class="modal" :class="{ 'is-active': true }">
        <div class="modal-background" @click.stop="showCancelOrderModal = false"></div>
        <div class="modal-content">
          <div class="box modal-container" style="overflow: hidden">
            <div class="modal-content-root">
              <div class="title-font is-size-4 mainColor--text mb-4">
                You’re about to cancel this order
              </div>
              <div class="mainColor--text mb-2" style="font-size: 1rem; line-height: 1.6;">
                When you cancel the order, <b style="font-size: 1rem; line-height: 1.6;">the pending invoice will be deleted and you won't be charged.</b>
              </div>
              <div class="mainColor--text" style="font-size: 1rem; line-height: 1.6;">
                Rest assured, original order data won't be lost, and you can always make changes to the order and submit it again.
              </div>
              <div class="flex mt20 justify-space-between">
                <v-btn
                  outlined
                  color="mainColor"
                  class="button-with-icon mainColor--text text-none"
                  @click.stop="showCancelOrderModal = false"
                >
                  <span>Abort</span>
                </v-btn>
                <v-btn
                  color="profileAnchorColor"
                  class="button-with-icon text-none whiteColor--text"
                  @click.stop="updateOrder({ cancel_order: true })"
                >
                  <span>Cancel this order now</span>
                </v-btn>
              </div>
            </div>
          </div>
          <button
            class="custom-modal-close-btn"
            aria-label="close"
            @click.stop="showCancelOrderModal = false"
          >
            <i class="material-icons">close</i>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import SiloResultTable from '@/components/Campaigns/silo/SiloResultTable.vue'
import BulkRunModal from '@/components/BulkRunModal.vue'
import BulkMoveToSiloModal from '@/components/Campaigns/silo/BulkMoveToSiloModal.vue'
import { ALLOWED_SILO_ORDER_DELIVERY_PERIODS, whiteGlovePlans } from '@/components/plans_constants'
import { getFulfillementStatusLabel } from '@/components/Campaigns/content-orders/utils'
import { getPureServicePkgName, getHostName } from '@/utils'
const channel = new BroadcastChannel('silo_content_editor_channel')
const aiWriteChannel = new BroadcastChannel('silo_ai_writer_channel')

function pathFromKeywordUrl (url) {
  if (!url || typeof url !== 'string') return null
  const t = url.trim()
  if (!t) return null
  try {
    if (/^https?:\/\//i.test(t)) {
      const pathname = new URL(t).pathname
      return pathname || '/'
    }
    if (t.startsWith('/')) {
      return t.split('?')[0]
    }
    return `/${t.split('?')[0]}`
  } catch {
    return t
  }
}

function primaryKeywordRow (siloKeywords) {
  const kws = (siloKeywords || []).filter((k) => !k?.isDeleted)
  const primary = kws.find(
    (k) => k.keywordData?.isPrimaryKeyword && k.keywordData?.isTopLevelKeyword,
  )
  if (primary) return primary
  const top = kws.find(
    (k) => k.keywordData?.isTopLevelKeyword && !k.keywordData?.isSupportingKeyword,
  )
  if (top) return top
  return kws.find((k) => !k.keywordData?.isSupportingKeyword) || kws[0]
}

export default {
  name: 'ContentOnlyDetailWhiteGlove',
  components: {
    SiloResultTable,
    BulkRunModal,
    BulkMoveToSiloModal,
  },
  data () {
    return {
      silo: null,
      silo_keywords: [],
      silos: [],
      quickContentSilos: [],
      showCancelOrderModal: false,
      bulkPopUp: false,
      bulkMovePopUp: false,
      currentCampaign: null,
    }
  },
  computed: {
    curSiloId () {
      const id = this.$route.params.siloId
      return id ? Number(id) : null
    },
    siteId () {
      return Number(this.$route.params.siteId)
    },
    site () {
      return this.$store.getters.siteById(this.siteId) || {}
    },
    campaignId () {
      return this.silo?.campaignId
    },
    selectedSiloKeywords () {
      return this.$store.getters.selectedSiloKeywords
    },
    selectedSiloKeywordsRows () {
      return this.silo_keywords.filter((sk) => this.selectedSiloKeywords.includes(sk.id))
    },
    isWhiteGloveUser () {
      const user = this.$store.state.user
      if (!user) return false
      const accountType = this.$store.state.selectedSubAccountRelation
        ? this.$store.state.selectedSubAccountRelation.ownerAccountType
        : user.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    activeKeywords () {
      return this.silo_keywords.filter((k) => !k.isDeleted)
    },
    deletedKeywords () {
      return this.silo_keywords.filter((k) => k.isDeleted)
    },
    allOrderedSiloKeywords () {
      return [
        ...this.silos
          .filter((s) => s?.content_report_order && s?.content_report_order?.order_status !== 'user_editing')
          .flatMap((s) => s.silo_keywords.filter((k) => !k.isDeleted).map((sk) => sk.keyword)),
        ...this.quickContentSilos
          .filter((s) => s?.content_report_order && s?.content_report_order?.order_status !== 'user_editing')
          .flatMap((s) => s.silo_keywords.filter((k) => !k.isDeleted).map((sk) => sk.keyword)),
      ]
    },
    pageTypeLabel () {
      if (this.silo?.pageType === 'new') return 'New page'
      if (this.silo?.pageType === 'existing') return 'Existing page'
      return ''
    },
    mainPagePathDisplay () {
      const row = primaryKeywordRow(this.silo_keywords)
      if (!row) return '—'
      const url = row.keywordData?.page || row.keywordData?.currentRankingUrl
      const path = pathFromKeywordUrl(url)
      if (path) return path
      const kw = row.keyword?.trim()
      return kw || '—'
    },
    keywordCount () {
      return this.activeKeywords.length
    },
    keywordMetricLabel () {
      const hasPrimaryTopLevelKeyword = this.activeKeywords.some(
        (keyword) => keyword.keywordData?.isTopLevelKeyword && keyword.keywordData?.isPrimaryKeyword,
      )
      const counts = this.activeKeywords.reduce((acc, keyword) => {
        const keywordData = keyword.keywordData || {}
        if (keywordData.isSupportingKeyword) {
          acc.supporting += 1
          return acc
        }
        if (keywordData.isTopLevelKeyword) {
          const isSecondaryTopLevel = keywordData.isSecondaryKeyword
            || (hasPrimaryTopLevelKeyword && !keywordData.isPrimaryKeyword)
          if (isSecondaryTopLevel) {
            acc.secondaryTopLevel += 1
          } else {
            acc.topLevel += 1
          }
        }
        return acc
      }, {
        topLevel: 0,
        secondaryTopLevel: 0,
        supporting: 0,
      })
      const parts = [
        counts.topLevel > 0 ? `${counts.topLevel} Top-level` : null,
        counts.secondaryTopLevel > 0 ? `${counts.secondaryTopLevel} Secondary top-level` : null,
        counts.supporting > 0 ? `${counts.supporting} Supporting` : null,
      ].filter(Boolean)
      return parts.join(' / ')
    },
    estimatedMonthlyVisibility () {
      const v = this.silo?.estimatedMonthlyVisibility
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    estTrafficFormatted () {
      return `${this.estimatedMonthlyVisibility.toLocaleString()}/mo`
    },
    order () {
      return this.silo?.content_report_order || null
    },
    orderedLike () {
      const st = this.order?.order_status
      return ['paid', 'to_be_paid_by_invoice'].includes(st)
    },
    selectedOrderCount () {
      if (!this.order) return 0
      const t = this.order.selected_top_level_keyword_ids?.length || 0
      const s = this.order.selected_supporting_keyword_ids?.length || 0
      return t + s
    },
    orderedSelectionLine () {
      const total = this.keywordCount || 0
      const sel = this.order ? this.selectedOrderCount : 0
      return `${sel} of ${total}`
    },
    orderedSelectionMetricLabel () {
      return this.order?.order_status === 'paid' ? 'Ordered' : 'Selected'
    },
    showOrderMetricsCard () {
      
      return false
      return this.order && this.orderedLike
    },
    deliveryPeriodLabel () {
      const days = this.order?.delivery_period_days
      if (days == null) return ''
      const entry = ALLOWED_SILO_ORDER_DELIVERY_PERIODS[days]
      return entry?.label || ''
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    whiteGloveMissionControlAdminUi () {
      return this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
  },
  watch: {
    '$store.state.refreshDataAfterCartPurchase' () {
      this.loadData()
    },
    curSiloId (next, prev) {
      if (next !== prev && prev !== undefined) {
        this.loadData()
      }
    },
    siteId (next, prev) {
      if (next !== prev && prev !== undefined) {
        this.loadData()
      }
    },
  },
  mounted () {
    this.$store.commit('resetSelectedSiloKeywords')
    this.loadData()
    channel.onmessage = (message) => {
      if (message.data === 'flag_updated') {
        this.loadData()
      }
    }
    aiWriteChannel.onmessage = (message) => {
      if (message.data === 'ai_writer_updated') {
        this.loadData()
      }
    }
  },
  beforeDestroy () {
    channel.close()
    aiWriteChannel.close()
  },
  methods: {
    getFulfillementStatusLabel,
    formatOrderDeliveryPeriodStart (value) {
      if (!value) return ''
      const m = moment.utc(value)
      return m.isValid() ? m.format('ddd, DD MMM YYYY HH:mm:ss [GMT]') : String(value)
    },
    formatOrderDueAt (value) {
      if (!value) return ''
      const m = moment.utc(value)
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(value)
    },
    updateOrder (payload) {
      if (!this.order?.id) return
      this.$store.commit('showLoading')
      this.$store
        .dispatch('updateSiloContentReportOrder', {
          orderId: this.order.id,
          body: payload,
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            if (payload?.cancel_order) {
              this.showCancelOrderModal = false
            }
            this.loadData()
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    updateSilo (payload, refresh = false) {
      if (!this.silo?.id) return Promise.resolve()
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('updateSilo', {
          siloId: this.silo.id,
          body: payload,
        })
        .then(() => {
          if (refresh) {
            return this.loadData(true)
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    fetchSilos () {
      const campaignId = this.silo?.campaignId
      if (!campaignId) return Promise.resolve()
      // only keyword names + content_report_order are read from sibling silos
      return Promise.all([
        this.$store.dispatch('getSilos', {
          campaignId,
          paginate: 0,
          includeKeywords: 1,
          includeAiAndPageRunResult: 0,
          includeGuardDiffData: 0,
          includePage: 0,
        }),
        this.$store.dispatch('getSilos', {
          campaignId,
          paginate: 0,
          isForQuickContentOrder: 1,
          includeKeywords: 1,
          includeAiAndPageRunResult: 0,
          includeGuardDiffData: 0,
          includePage: 0,
        }),
      ]).then(([plansResponse, quickResponse]) => {
        this.silos = plansResponse.data.data || []
        this.quickContentSilos = quickResponse.data.data || []
      })
    },
    loadData (silent = false) {
      if (!this.curSiloId) return Promise.resolve()
      if (!silent) {
        this.$store.commit('showLoading')
      }
      // Keywords only depend on the silo ID, so fetch them concurrently with
      // the silo itself. The guard keeps a rejection from surfacing as
      // unhandled when a redirect below abandons the result.
      const keywordsPromise = this.$store.dispatch('getSiloKeywords', {
        silo_id: this.curSiloId,
        paginate: 0,
        includeDeleted: 1,
        includeAiAndPageRunResult: 1,
        includeGuardDiffData: 0,
      })
      keywordsPromise.catch(() => {})
      return this.$store
        .dispatch('getSilo', this.curSiloId)
        .then((response) => {
          const data = response.data.data
          if (!data?.isForQuickContentOrder) {
            this.$router.replace({
              name: 'ContentPlanDetailWhiteGlove',
              params: {
                siteId: String(this.siteId),
                siloId: String(data?.id ?? this.curSiloId),
              },
            })
            return
          }
          const siloSiteId = Number(data?.siteId)
          if (data && Number.isFinite(siloSiteId) && siloSiteId !== this.siteId) {
            this.$router.replace({
              name: 'AllContentWhiteGlove',
              params: { siteId: String(this.siteId) },
            })
            return
          }
          this.silo = data
          this.currentCampaign = null
          const campaignPromise = data.campaignId
            ? this.$store.dispatch('getCampaignById', data.campaignId).then((campResponse) => {
              if (campResponse.data?.status === 'SUCCESS') {
                this.currentCampaign = campResponse.data.campaign
              }
            })
            : Promise.resolve()

          return Promise.all([
            campaignPromise,
            keywordsPromise.then((kwResponse) => {
              this.silo_keywords = kwResponse.data.data || []
            }),
            this.fetchSilos(),
          ])
        })
        .finally(() => {
          if (!silent) {
            this.$store.commit('hideLoading')
          }
        })
    },
    onKeywordRemoveSuccess (keywordId) {
      this.silo_keywords = this.silo_keywords.map((sk) => ({
        ...sk,
        isDeleted: sk.id === keywordId ? true : sk.isDeleted,
      }))
      this.refreshEstimatedMonthlyVisibilityFromServer()
    },
    onKeywordRestoreSuccess (keywordId) {
      this.silo_keywords = this.silo_keywords.map((sk) => ({
        ...sk,
        isDeleted: sk.id === keywordId ? false : sk.isDeleted,
      }))
      this.refreshEstimatedMonthlyVisibilityFromServer()
    },
    bulkRemoveKeyword () {
      const selected = [...this.selectedSiloKeywords]
      const tableRef = this.$refs['silo-result-table']
      if (!tableRef) return
      tableRef.keywordsOngoingRemoval = [...tableRef.keywordsOngoingRemoval, ...selected]
      this.$store
        .dispatch('bulkRemoveKeywordFromSilo', {
          siloId: this.silo.id,
          keywordIds: selected,
        })
        .then((response) => {
          if (response?.data?.status === 'SUCCESS') {
            return this.loadData(true)
          }
          this.$notify({
            group: 'info',
            type: 'error',
            text: response?.data?.msg || 'Failed!',
          })
        })
        .finally(() => {
          tableRef.keywordsOngoingRemoval = tableRef.keywordsOngoingRemoval.filter(
            (k) => !selected.includes(k),
          )
          this.resetSelectedSiloKeywords()
        })
    },
    bulkTrackKeyword () {
      if (!this.currentCampaign) {
        this.$notify({
          group: 'info',
          type: 'error',
          text: 'Campaign data is still loading. Try again in a moment.',
        })
        return
      }
      if (!this.campaignId) return
      const selectedKeywords = this.silo_keywords.filter((sk) =>
        this.selectedSiloKeywords.includes(sk.id),
      )
      const data = {
        campaignId: this.campaignId,
        trackKeywords: selectedKeywords,
        languageCode: this.currentCampaign.languageCode,
        locationCode: this.currentCampaign.locationCode,
        domain: getHostName(this.currentCampaign.targetUrl),
      }
      this.$store.commit('showLoading')
      this.$store
        .dispatch('bulkTrackKeywordToTrack', data)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            response.data.added && this.$store.commit('setIsNewTrackingUpdates', true)
            this.$refs['silo-result-table']?.loadKeywordTrack()
            this.$notify({
              group: 'info',
              type: 'success',
              text: response.data.added
                ? 'POP is now tracking these keywords.'
                : 'Keywords are removed from tracking.',
            })
          } else {
            this.$notify({
              group: 'info',
              type: 'error',
              text: 'Failed to update keywords.',
            })
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
          this.resetSelectedSiloKeywords()
        })
    },
    orderContentForSelectedKeywords () {
      const selectedKeywords = this.silo_keywords.filter((sk) =>
        this.selectedSiloKeywords.includes(sk.id),
      )
      const topLevelKeywordIds = selectedKeywords
        .filter((row) => row.keywordData.isTopLevelKeyword)
        .map((row) => row.id)
      const supportingKeywordsIds = selectedKeywords
        .filter((row) => row.keywordData.isSupportingKeyword)
        .map((row) => row.id)

      this.$store
        .dispatch('updateSiloContentReportOrder', {
          orderId: this.silo.content_report_order.id,
          body: {
            selected_top_level_keyword_ids: topLevelKeywordIds,
            selected_supporting_keyword_ids: supportingKeywordsIds,
          },
        })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.loadData(true)
            this.resetSelectedSiloKeywords()
            if (this.$refs['silo-result-table']) {
              this.$refs['silo-result-table'].showContentOrderFormModal = true
            }
          }
        })
    },
    createBulkReport () {
      const selectedKeywords = this.silo_keywords.filter((sk) =>
        this.selectedSiloKeywords.includes(sk.id),
      )
      const body = selectedKeywords.map((siloKeyword) => {
        const isTopLevel = siloKeyword.keywordData.isTopLevelKeyword
        const config = this.site.settings
        const url = siloKeyword.keywordData.page || siloKeyword.keywordData.currentRankingUrl

        return {
          silo_keyword_id: siloKeyword.id,
          project_name: this.site.name,
          proxy_location: config.proxyLocation,
          target_language: config.targetLang,
          ignore_spaces: config.ignoreSpaces,
          page_name: siloKeyword.keyword,
          competitor_urls: [],
          page_not_built: isTopLevel && url ? 0 : 1,
          keyword: siloKeyword.keyword,
          url: url || this.site.baseURL,
          tab_name: 'silo bulk run',
          variations: [],
          pop_pick_competitors: 'yes',
        }
      })
      this.$store.commit('showLoading')
      this.$store
        .dispatch('generateNewBulkRun', body)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            const bulkRunId = response.data.bulkRun.id
            this.$store.dispatch('createNewBulkRun', { bulkRunId, isSilo: true }).then(() => {
              this.loadData()
            })
            this.closeBulkModal()
            this.resetSelectedSiloKeywords()
          } else if (response.data && response.data.msg) {
            this.$notify({ group: 'info', type: 'error', text: response.data.msg, duration: 5000 })
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    closeBulkModal () {
      this.bulkPopUp = false
    },
    openBulkModal () {
      this.bulkPopUp = true
    },
    resetSelectedSiloKeywords () {
      this.$store.commit('resetSelectedSiloKeywords')
    },
    refreshEstimatedMonthlyVisibilityFromServer () {
      if (!this.curSiloId || !this.silo) return Promise.resolve()
      return this.$store.dispatch('getSilo', this.curSiloId).then((response) => {
        if (response.data?.status !== 'SUCCESS') return
        const d = response.data.data
        if (!d) return
        this.silo = {
          ...this.silo,
          estimatedMonthlyVisibility: d.estimatedMonthlyVisibility,
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/components/Campaigns/silo/silo.scss';

.wg-content-only-detail {
  padding: 8px 0 40px;
}

.wg-content-only-detail__back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-profileAnchorColor-base);
  text-decoration: none;

  &:hover {
    opacity: 0.88;
  }
}

.wg-content-only-detail__back-icon {
  font-size: 1.1rem;
}

.wg-content-only-detail__head {
  margin-bottom: 30px;
}

.wg-content-only-detail__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.wg-content-only-detail__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.3;
}

.wg-content-only-detail__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #e8f5e9;
  color: #2e7d32;
}

.wg-content-only-detail__badge--quick {
  background: #fff3e0;
  color: #e65100;
}

.wg-content-only-detail__badge--trigger {
  border: none;
  margin: 0;
  font: inherit;
  line-height: inherit;
  cursor: pointer;
  gap: 4px;
}

.wg-content-only-detail__badge--assign {
  background: #fff;
  color: #6a6a6a;
  border: 1px dashed #ccc;
  padding: 4px 10px;
}

.wg-content-only-detail__badge-caret {
  flex-shrink: 0;
  font-size: 1rem !important;
  opacity: 0.75;
}

.wg-content-only-detail__badge-assign-icon {
  flex-shrink: 0;
  font-size: 1rem !important;
}

::v-deep .wg-content-only-detail__page-type-dropdown .dropdown-trigger {
  display: inline-flex;
}

::v-deep .wg-content-only-detail__page-type-dropdown .dropdown-item {
  font-weight: normal;
}

::v-deep .wg-content-only-detail__page-type-dropdown.is-disabled {
  opacity: initial;
  cursor: default;
}

.wg-content-only-detail__path {
  margin: 0;
  font-size: 0.8rem;
  color: var(--v-gray2Color-base, #757575);
  word-break: break-all;
}

.wg-content-only-detail__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) minmax(0, 1.4fr);
  gap: 16px;
  margin-bottom: 16px;
}

@media (max-width: 960px) {
  .wg-content-only-detail__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .wg-content-only-detail__metric--wide {
    grid-column: 1 / -1;
  }
}

@media (max-width: 520px) {
  .wg-content-only-detail__metrics {
    grid-template-columns: 1fr;
  }
}

.wg-content-only-detail__metric {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
}

.wg-content-only-detail__metric--wide {
  grid-column: span 1;
}

@media (min-width: 961px) {
  .wg-content-only-detail__metric--wide {
    grid-column: 4;
    grid-row: 1;
  }
}

.wg-content-only-detail__metric-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
  line-height: 1.3;
}

.wg-content-only-detail__metric-label {
  margin-top: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--v-gray2Color-base, #757575);
  letter-spacing: 0.02em;
}

.wg-content-only-detail__metric-label--tooltip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: help;
}

.wg-content-only-detail__metric-info-icon {
  font-size: 14px;
  opacity: 0.65;
}

.wg-content-only-detail__emv-tooltip-bubble {
  max-width: 320px;
}

.wg-content-only-detail__emv-tooltip-text {
  font-size: 0.85rem;
  line-height: 1.4;
}

.wg-content-only-detail__order-card {
  border-color: var(--v-gray12Color-base, #d9d9d9);
  padding: 12px 14px;
}

.wg-content-only-detail__order-card-title {
  margin: 0 0 8px;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--v-mainColor-base);
}

.wg-content-only-detail__order-line {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25em 0.4em;
  margin-bottom: 5px;
  font-size: 0.8125rem;
  line-height: 1.35;

  &:last-child {
    margin-bottom: 0;
  }
}

.wg-content-only-detail__order-line--admin {
  align-items: center;
}

.wg-content-only-detail__order-line-break {
  flex-basis: 100%;
  width: 100%;
}

.wg-content-only-detail__order-k {
  color: var(--v-gray2Color-base, #757575);
  font-weight: 400;
}

.wg-content-only-detail__order-v {
  color: var(--v-mainColor3-base, #171717);
  font-weight: 700;
}

.wg-content-only-detail__order-status-inline {
  display: inline-flex;
  align-items: center;
}

.wg-content-only-detail__order-invoice-note {
  margin-top: 3px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--v-profileAnchorColor-base);
}

.wg-content-only-detail__cancel-order-btn {
  margin-top: 4px;
}

.wg-content-only-detail__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 18px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: var(--v-pink1Color-base, #f7f7fc);
  border-radius: 8px;
  font-size: 0.8rem;
  color: var(--v-mainColor-base);
}

.wg-content-only-detail__legend-title {
  font-weight: 700;
  margin-right: 4px;
}

.wg-content-only-detail__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.wg-content-only-detail__legend-hint {
  margin-left: auto;
  color: var(--v-gray2Color-base, #757575);
  font-size: 0.78rem;
}

@media (max-width: 768px) {
  .wg-content-only-detail__legend-hint {
    margin-left: 0;
    flex-basis: 100%;
  }
}

.wg-content-only-detail__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
}

.wg-content-only-detail__tag--tl {
  background: #e8f5e9;
  color: #2e7d32;
}

.wg-content-only-detail__tag--stl {
  background: #e3f2fd;
  color: #1565c0;
}

.wg-content-only-detail__tag--s {
  background: #fce4ec;
  color: #c2185b;
}
</style>
